// contexts/SubscriptionContext.tsx
// Contexte global pour gérer l'état d'abonnement dans toute l'app

import {
  getOfferings,
  isRevenueCatAvailable,
  purchasePackage,
  RCOffering,
  RCPackage,
  restorePurchases
} from '@/lib/revenuecat';
import { supabase } from '@/lib/supabase';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Linking, Platform } from 'react-native';

const isWeb = Platform.OS === 'web';

// ===== TYPES =====

interface UserProfile {
  id: string;
  full_name: string | null;
  diploma: string | null;
  section: string | null;
  subject_codes: string[] | null;
  premium: boolean;
  subscription_source: 'apple' | 'google' | 'stripe' | null;
  xp: number;
  streak_days: number;
  best_streak: number;
  last_activity_date: string | null;
  onboarding_completed: boolean;
  push_token: string | null;
  notifications_enabled: boolean;
}

interface SubscriptionDetails {
  subscribed: boolean;
  cancelAtPeriodEnd: boolean;
  subscriptionEnd: string | null;
  planType: string | null;
  subscriptionSource: 'apple' | 'google' | 'stripe' | null;
}

interface SubscriptionContextType {
  // Profile data
  profile: UserProfile | null;
  profileLoading: boolean;
  
  // Subscription state
  isPremium: boolean;
  subscriptionDetails: SubscriptionDetails;
  subscriptionLoading: boolean;
  
  // RevenueCat
  offerings: RCOffering | null;
  isRevenueCatReady: boolean;
  canUseInAppPurchase: boolean;
  
  // Actions
  subscribe: (plan: 'monthly' | 'yearly') => Promise<boolean>;
  restorePurchases: () => Promise<boolean>;
  refreshProfile: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  
  // Helpers
  isWeb: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// ===== PROVIDER =====

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  
  // Subscription state
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails>({
    subscribed: false,
    cancelAtPeriodEnd: false,
    subscriptionEnd: null,
    planType: null,
    subscriptionSource: null,
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // RevenueCat state
  const [offerings, setOfferings] = useState<RCOffering | null>(null);
  const [isRevenueCatReady, setIsRevenueCatReady] = useState(false);

  // ===== FETCH PROFILE =====
  const fetchProfile = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfile(null);
        setProfileLoading(false);
        return null;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const profileData = data as UserProfile;
      setProfile(profileData);
      return profileData;
    } catch (err) {
      console.error('Error fetching profile:', err);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  // ===== FETCH SUBSCRIPTION DETAILS =====
  const fetchSubscriptionDetails = useCallback(async (userId: string, isPremium: boolean, source: string | null) => {
    try {
      if (isPremium) {
        const { data: subscription } = await supabase
          .from('subscriptions')
          .select('current_period_end, cancel_at_period_end, plan_type, status')
          .eq('user_id', userId)
          .in('status', ['active', 'trialing'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        setSubscriptionDetails({
          subscribed: true,
          cancelAtPeriodEnd: subscription?.cancel_at_period_end || false,
          subscriptionEnd: subscription?.current_period_end || null,
          planType: subscription?.plan_type || null,
          subscriptionSource: source as 'apple' | 'google' | 'stripe' | null,
        });
      } else {
        setSubscriptionDetails({
          subscribed: false,
          cancelAtPeriodEnd: false,
          subscriptionEnd: null,
          planType: null,
          subscriptionSource: null,
        });
      }
    } catch (err) {
      console.error('Error fetching subscription details:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  // ===== LOAD OFFERINGS =====
  const loadOfferings = useCallback(async () => {
    if (isWeb || !isRevenueCatAvailable()) return;
    
    try {
      const rcOfferings = await getOfferings();
      setOfferings(rcOfferings);
      setIsRevenueCatReady(rcOfferings !== null);
    } catch (err) {
      console.error('Error loading offerings:', err);
    }
  }, []);

  // ===== INITIALIZE =====
  useEffect(() => {
    const initialize = async () => {
      const profileData = await fetchProfile();
      
      if (profileData) {
        await fetchSubscriptionDetails(
          profileData.id, 
          profileData.premium, 
          profileData.subscription_source
        );
      } else {
        setSubscriptionLoading(false);
      }
      
      await loadOfferings();
    };

    initialize();
  }, [fetchProfile, fetchSubscriptionDetails, loadOfferings]);

  // ===== REALTIME SUBSCRIPTION =====
  useEffect(() => {
    let channel: any = null;

    const setupRealtime = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      channel = supabase
        .channel(`profile-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${user.id}`,
          },
          async (payload) => {
            console.log('🔄 Profile updated via Realtime:', payload.new);
            const newProfile = payload.new as UserProfile;
            setProfile(newProfile);
            
            // Mettre à jour les détails d'abonnement si le statut premium a changé
            await fetchSubscriptionDetails(
              newProfile.id,
              newProfile.premium,
              newProfile.subscription_source
            );
          }
        )
        .subscribe((status: string) => {
          console.log('📡 Realtime subscription status:', status);
        });
    };

    setupRealtime();

    // Écouter les changements d'auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          await fetchProfile();
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          setSubscriptionDetails({
            subscribed: false,
            cancelAtPeriodEnd: false,
            subscriptionEnd: null,
            planType: null,
            subscriptionSource: null,
          });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchProfile, fetchSubscriptionDetails]);

  // ===== REFRESH FUNCTIONS =====
  const refreshProfile = useCallback(async () => {
    setProfileLoading(true);
    const profileData = await fetchProfile();
    if (profileData) {
      await fetchSubscriptionDetails(
        profileData.id,
        profileData.premium,
        profileData.subscription_source
      );
    }
  }, [fetchProfile, fetchSubscriptionDetails]);

  const refreshSubscription = useCallback(async () => {
    if (!profile) return;
    setSubscriptionLoading(true);
    await fetchSubscriptionDetails(
      profile.id,
      profile.premium,
      profile.subscription_source
    );
  }, [profile, fetchSubscriptionDetails]);

  // ===== PURCHASE WITH STRIPE =====
  const purchaseWithStripe = async (plan: 'monthly' | 'yearly'): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        Alert.alert('Erreur', 'Tu dois être connecté pour t\'abonner.');
        return false;
      }

      const planType = plan === 'yearly' ? 'premium_yearly' : 'premium_monthly';
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planType },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        Alert.alert('Erreur', error.message || 'Une erreur est survenue.');
        return false;
      }

      if (data?.url) {
        if (isWeb) {
          window.location.href = data.url;
        } else {
          await Linking.openURL(data.url);
        }
        return true;
      }
      
      Alert.alert('Erreur', 'Impossible de créer la session de paiement.');
      return false;
    } catch (error: any) {
      Alert.alert('Erreur', error.message || 'Une erreur est survenue.');
      return false;
    }
  };

  // ===== PURCHASE WITH REVENUECAT =====
  const purchaseWithRevenueCat = async (packageToPurchase: RCPackage): Promise<boolean> => {
    try {
      const result = await purchasePackage(packageToPurchase);
      
      if (result.success) {
        // Attendre que le webhook traite l'achat
        console.log('⏳ Waiting for webhook to process...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Forcer le refresh du profil depuis Supabase
        console.log('🔄 Refreshing profile...');
        await refreshProfile();
        
        Alert.alert(
          '🎉 Bienvenue dans Ernest Premium !',
          'Tu as maintenant accès à toutes les fonctionnalités.',
          [{ text: 'Super !' }]
        );
        return true;
      } else if (result.error === 'cancelled') {
        return false;
      } else {
        Alert.alert('Erreur', result.error || 'Une erreur est survenue.');
        return false;
      }
    } catch (error: any) {
      Alert.alert('Erreur', 'Une erreur est survenue lors de l\'achat.');
      return false;
    }
  };

  // ===== MAIN SUBSCRIBE FUNCTION =====
  const subscribe = async (plan: 'monthly' | 'yearly'): Promise<boolean> => {
    if (isWeb) {
      return await purchaseWithStripe(plan);
    }
    
    if (isRevenueCatReady && offerings) {
      const packageToUse = plan === 'yearly' ? offerings.annual : offerings.monthly;
      if (packageToUse) {
        return await purchaseWithRevenueCat(packageToUse);
      }
    }
    
    return await purchaseWithStripe(plan);
  };

  // ===== RESTORE PURCHASES =====
  const handleRestorePurchases = async (): Promise<boolean> => {
    if (isWeb) {
      Alert.alert('Info', 'La restauration n\'est disponible que sur mobile.');
      return false;
    }
    
    try {
      const result = await restorePurchases();
      
      if (result.success && result.isPremium) {
        // ✅ Mettre à jour Supabase directement
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const subscriptionSource = Platform.OS === 'ios' ? 'apple' : 'google';
          
          const { error } = await supabase
            .from('profiles')
            .update({ 
              premium: true,
              subscription_source: subscriptionSource,
            })
            .eq('id', user.id);
          
          if (error) {
            console.error('Error updating profile after restore:', error);
          } else {
            console.log('✅ Profile updated after restore');
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        await refreshProfile();
        
        Alert.alert('✅ Achats restaurés', 'Ton abonnement Premium a été restauré !');
        return true;
      } else if (result.success) {
        Alert.alert('Aucun achat trouvé', 'Aucun abonnement actif n\'a été trouvé.');
        return false;
      } else {
        Alert.alert('Erreur', result.error || 'Impossible de restaurer les achats.');
        return false;
      }
    } catch (error) {
      Alert.alert('Erreur', 'Une erreur est survenue.');
      return false;
    }
  };

  // ===== OPEN CUSTOMER PORTAL =====
  const openCustomerPortal = async () => {
    const source = subscriptionDetails.subscriptionSource;
    
    if (source === 'apple') {
      const url = 'https://apps.apple.com/account/subscriptions';
      await Linking.openURL(url);
    } else if (source === 'google') {
      const url = 'https://play.google.com/store/account/subscriptions';
      await Linking.openURL(url);
    } else if (source === 'stripe') {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          Alert.alert('Erreur', 'Tu dois être connecté.');
          return;
        }

        const { data, error } = await supabase.functions.invoke('create-billing-portal', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (error) throw error;
        if (data?.url) {
          if (isWeb) {
            window.location.href = data.url;
          } else {
            await Linking.openURL(data.url);
          }
        }
      } catch (error: any) {
        Alert.alert('Erreur', error.message || 'Impossible d\'ouvrir le portail.');
      }
    } else {
      // Fallback selon la plateforme
      if (Platform.OS === 'ios') {
        await Linking.openURL('https://apps.apple.com/account/subscriptions');
      } else if (Platform.OS === 'android') {
        await Linking.openURL('https://play.google.com/store/account/subscriptions');
      }
    }
  };

  // ===== CONTEXT VALUE =====
  const value: SubscriptionContextType = {
    profile,
    profileLoading,
    isPremium: profile?.premium || false,
    subscriptionDetails,
    subscriptionLoading,
    offerings,
    isRevenueCatReady,
    canUseInAppPurchase: !isWeb && isRevenueCatReady && offerings !== null,
    subscribe,
    restorePurchases: handleRestorePurchases,
    refreshProfile,
    refreshSubscription,
    openCustomerPortal,
    isWeb,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// ===== HOOK =====
export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}

// ===== BACKWARD COMPATIBILITY HOOKS =====
// Pour éviter de modifier tous les fichiers d'un coup

export function useUserProfile() {
  const { profile, profileLoading, refreshProfile } = useSubscriptionContext();
  return {
    profile,
    loading: profileLoading,
    error: null,
    refetch: refreshProfile,
    isPremium: profile?.premium || false,
    isOnboardingCompleted: profile?.onboarding_completed || false,
  };
}

export function useSubscription() {
  const ctx = useSubscriptionContext();
  return {
    subscribed: ctx.isPremium,
    cancelAtPeriodEnd: ctx.subscriptionDetails.cancelAtPeriodEnd,
    subscriptionEnd: ctx.subscriptionDetails.subscriptionEnd,
    planType: ctx.subscriptionDetails.planType,
    subscriptionSource: ctx.subscriptionDetails.subscriptionSource,
    loading: ctx.subscriptionLoading,
    offerings: ctx.offerings,
    isRevenueCatReady: ctx.isRevenueCatReady,
    subscribe: ctx.subscribe,
    restorePurchases: ctx.restorePurchases,
    refreshSubscription: ctx.refreshSubscription,
    openCustomerPortal: ctx.openCustomerPortal,
    canUseInAppPurchase: ctx.canUseInAppPurchase,
    isWeb: ctx.isWeb,
  };
}
